import { useEffect, useRef, useState } from 'react';

import Search from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router';

import { get } from '@/api/client';
import { RUTAS } from '@/api/rutas';
import { CodigoDePieza } from '@/components/CodigoDePieza';
import type { Paginacion, Producto } from '@/tipos';

const CUANTOS = 6;
const ESPERA = 250;

/**
 * La búsqueda, en la portada.
 *
 * El trabajo del sitio es encontrar una pieza. Hasta ahora la portada abría con el banner y el
 * buscador vivía dentro del catálogo, a un clic y un scroll de distancia: la primera pantalla
 * contaba quién es CKING y no servía para lo que la gente viene a hacer.
 *
 * Va debajo del banner, no en su lugar. El banner sigue siendo lo primero.
 *
 * Los resultados salen mientras se escribe, contra el mismo endpoint que usa el catálogo. Enter
 * lleva al catálogo con la búsqueda puesta, así que escribir y apretar Enter hace lo de siempre
 * aunque no se mire la lista.
 */
export function BuscadorDestacado() {
  const navigate = useNavigate();
  const campo = useRef<HTMLInputElement | null>(null);

  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<Producto[]>([]);
  const [total, setTotal] = useState(0);
  const [buscando, setBuscando] = useState(false);
  const [abierto, setAbierto] = useState(false);

  // `/` y Ctrl+K son los dos atajos que la gente ya prueba sin que nadie se los diga. El `/` se
  // ignora mientras se está escribiendo en otro campo: si no, no se podría escribir una barra.
  useEffect(() => {
    function alApretar(evento: KeyboardEvent) {
      const enUnCampo =
        evento.target instanceof HTMLElement &&
        ['INPUT', 'TEXTAREA'].includes(evento.target.tagName);

      const atajo =
        (evento.key === 'k' && (evento.ctrlKey || evento.metaKey)) || evento.key === '/';

      if (atajo && !enUnCampo) {
        evento.preventDefault();
        campo.current?.focus();
      }

      if (evento.key === 'Escape') setAbierto(false);
    }

    window.addEventListener('keydown', alApretar);
    return () => window.removeEventListener('keydown', alApretar);
  }, []);

  useEffect(() => {
    const buscado = texto.trim();

    if (buscado.length < 2) {
      setResultados([]);
      setTotal(0);
      setBuscando(false);
      return;
    }

    setBuscando(true);
    let vivo = true;

    const reloj = setTimeout(async () => {
      try {
        const { products, pagination } = await get<{
          products: Producto[];
          pagination: Paginacion;
        }>(RUTAS.catalogo.productos, { query: { search: buscado, page: 1, limit: CUANTOS } });

        if (!vivo) return;

        setResultados(products);
        setTotal(pagination.total);
      } catch {
        // Si la consulta falla, la lista queda vacía y Enter sigue llevando al catálogo. No es
        // el lugar para un cartel de error: es una ayuda, no el camino principal.
        if (vivo) {
          setResultados([]);
          setTotal(0);
        }
      } finally {
        if (vivo) setBuscando(false);
      }
    }, ESPERA);

    return () => {
      vivo = false;
      clearTimeout(reloj);
    };
  }, [texto]);

  function irAlCatalogo() {
    const buscado = texto.trim();
    if (!buscado) return;

    void navigate(`/catalogo?search=${encodeURIComponent(buscado)}`);
  }

  const hayLista = abierto && texto.trim().length >= 2;

  return (
    <Box sx={{ bgcolor: '#0a0a0a', py: { xs: 5, md: 7 } }}>
      <Container maxWidth="md">
        <Typography variant="h2" component="h2" sx={{ mb: 1, textAlign: 'center' }}>
          ¿Qué pieza estás buscando?
        </Typography>

        <Typography sx={{ color: 'text.secondary', textAlign: 'center', mb: 4 }}>
          Buscá por código o por descripción. Apretá <Tecla>/</Tecla> para saltar acá desde
          cualquier parte de la página.
        </Typography>

        <Box sx={{ position: 'relative' }}>
          <TextField
            inputRef={campo}
            fullWidth
            value={texto}
            onChange={evento => {
              setTexto(evento.target.value);
              setAbierto(true);
            }}
            onFocus={() => setAbierto(true)}
            // En blur con un retraso: sin esto, hacer clic en un resultado cierra la lista
            // antes de que el clic llegue al enlace.
            onBlur={() => setTimeout(() => setAbierto(false), 150)}
            onKeyDown={evento => {
              if (evento.key === 'Enter') irAlCatalogo();
            }}
            placeholder="ARB001, cadena de transmisión, buje…"
            aria-label="Buscar una pieza por código o descripción"
            // El mismo motivo que en el catálogo: sin esto Chrome despliega acá lo que la persona
            // escribió alguna vez en cualquier otro formulario, encima de la lista de resultados.
            autoComplete="off"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 28 }} />
                  </InputAdornment>
                ),
                endAdornment: buscando ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
                sx: {
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  bgcolor: '#161616',
                  '& input': { py: 2 },
                },
              },
            }}
          />

          {hayLista && (
            <Paper
              // Flota sobre lo que sigue: la sección de marcas no se mueve mientras se escribe.
              sx={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                right: 0,
                zIndex: 10,
                bgcolor: '#1a1a1a',
                border: '1px solid rgba(255,255,255,.08)',
                overflow: 'hidden',
              }}
            >
              {resultados.length === 0 ? (
                <Typography sx={{ p: 2.5, color: 'text.secondary' }}>
                  {buscando ? 'Buscando…' : 'Ninguna pieza responde a eso.'}
                </Typography>
              ) : (
                <>
                  {resultados.map(producto => (
                    <Box
                      key={producto.code}
                      component={Link}
                      to={`/catalogo/${encodeURIComponent(producto.code)}`}
                      viewTransition
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 2,
                        px: 2.5,
                        py: 1.5,
                        textDecoration: 'none',
                        color: 'inherit',
                        borderBottom: '1px solid rgba(255,255,255,.05)',
                        '&:hover, &:focus-visible': { bgcolor: 'rgba(230,0,0,.12)' },
                      }}
                    >
                      <CodigoDePieza
                        codigo={producto.code}
                        sx={{ color: 'primary.light', flexShrink: 0, minWidth: 110 }}
                      />

                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {producto.description}
                      </Typography>
                    </Box>
                  ))}

                  {total > resultados.length && (
                    <Box
                      component={Link}
                      to={`/catalogo?search=${encodeURIComponent(texto.trim())}`}
                      sx={{
                        display: 'block',
                        px: 2.5,
                        py: 1.5,
                        textDecoration: 'none',
                        color: 'primary.light',
                        fontWeight: 600,
                        '&:hover, &:focus-visible': { bgcolor: 'rgba(230,0,0,.12)' },
                      }}
                    >
                      Ver los {total} resultados
                    </Box>
                  )}
                </>
              )}
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
}

/** Una tecla, dibujada como una tecla. */
function Tecla({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="kbd"
      sx={{
        fontFamily: 'var(--font-roboto-condensed)',
        fontWeight: 700,
        fontSize: '0.85em',
        px: 0.75,
        py: 0.15,
        mx: 0.25,
        borderRadius: 0.75,
        border: '1px solid rgba(255,255,255,.25)',
        bgcolor: 'rgba(255,255,255,.06)',
      }}
    >
      {children}
    </Box>
  );
}
